import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useDispatch } from 'react-redux';

import {
  IProductCartItem,
  updateQuantityOfProductInCart,
} from 'store/modules/cart';
import { useNotices } from 'hooks';
import {
  createDebouncedDecrementHandler,
  createIncrementHandler,
} from 'utils/itemCounterFunctions';
import { formatCurrency } from 'utils/format';
import { getGACartItemsParams } from 'utils/gtag';

import ItemCounter from 'components/pages/PDP/ItemCounter';

import * as S from './styles';

interface IProductItemMiniCartProps {
  item: IProductCartItem;
}

const ProductItemMiniCart: React.FC<IProductItemMiniCartProps> = ({ item }) => {
  const dispatch = useDispatch();
  const timeoutRef = useRef(null);
  const { addNotices } = useNotices();
  const [quantity, setQuantity] = useState(item.quantidade);

  useEffect(() => {
    setQuantity(item.quantidade);
  }, [item]);

  const handleDeleteItem = useCallback(() => {
    addNotices({
      messages: [`Deseja remover o item "${item.nome}"?"`],
      type: 'warn',
      needConfirmation: true,
      action: () =>
        dispatch(
          updateQuantityOfProductInCart({
            sku: item.codigo,
            quantity: 0,
            showAfterRequest: false,
            GA: getGACartItemsParams({
              product: item,
              qty: item.quantidade,
              remove: true,
            }),
          }),
        ),
    });
  }, [addNotices, item.nome, item.codigo, dispatch]);

  const handleDecrementItem = useMemo(
    () =>
      createDebouncedDecrementHandler({
        quantity: quantity,
        item: item,
        dispatch,
        timeoutRef,
      }),
    [quantity, item, dispatch],
  );

  const handleIncrementItem = useMemo(
    () =>
      createIncrementHandler({
        item: item,
        dispatch,
      }),
    [item, dispatch],
  );

  const handleUpdateQuantityItem = useCallback(
    (quantityItens: number) => {
      dispatch(
        updateQuantityOfProductInCart({
          sku: item.codigo,
          quantity: quantityItens,
          showAfterRequest: false,
          GA: getGACartItemsParams({ product: item, qty: 1 }),
        }),
      );
    },
    [item, dispatch],
  );

  return (
    <S.Container className="product-item-mini-cart">
      <S.Content className="product-item-mini-cart-content">
        <div>
          <img
            className="product-item-image"
            loading="lazy"
            src={item.imagem}
            alt={item.nome}
          />

          <div className="info">
            <span className="product-name">{item.nome}</span>

            <div className="price-and-quantity">
              <div className="product-item-mini-cart-price-info">
                <div className="product-item-mini-cart-old-price-info">
                  R$
                  <span className="product-item-mini-cart-old-price">
                    {formatCurrency(item.precoDe)}
                    <span className="x-price-old"></span>
                  </span>
                </div>
                <div className="product-item-mini-cart-price-for-info">
                  <div>
                    <span className="product-item-mini-cart-price-for">
                      {item.precoPorTexto}
                    </span>
                  </div>
                </div>
              </div>

              <ItemCounter
                quantityMin={item.quantidadeMinimaVenda}
                quantityMax={item.quantidadeMaximaVenda}
                quantity={quantity}
                setQuantity={setQuantity}
                handleIncrementItem={handleIncrementItem}
                handleDecrementItem={handleDecrementItem}
                onChange={handleUpdateQuantityItem}
              />
            </div>
          </div>
        </div>

        <img
          src="/images/icons/trash-black.svg"
          alt="Excluir produto"
          className="icon trash-icon"
          onClick={handleDeleteItem}
        />
      </S.Content>

      {item.promocao !== null && (
        <div className="progressive-discount-group">
          <div className="progressive-discount-tag">
            {item.promocaoProgressivaImagemUrl ? (
              <img
                src={item.promocaoProgressivaImagemUrl}
                alt="Selo promoção"
                className="progressive-selo"
              />
            ) : (
              <span
                style={{
                  background: `${
                    item.promocaoProgressivaCor
                      ? `linear-gradient(to right, ${item.promocaoProgressivaCor})`
                      : 'linear-gradient(to right, var(--green_light), var(--green_blue))'
                  }`,
                }}
              >
                Desconto Progressivo
              </span>
            )}
            <span>Consulte a tabela</span>
          </div>

          <div className="progressive-discount-info">
            {item.promocaoProgressivaTexto.map(promo => (
              <p>{promo}</p>
            ))}
          </div>
        </div>
      )}
    </S.Container>
  );
};

export default ProductItemMiniCart;
